import * as pdfjsLib from "pdfjs-dist";

// Set up PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.js`;

// Add backend dimensions constants
export const BACKEND_WIDTH = 2550;
export const BACKEND_HEIGHT = 3300;

// Backend dimensions for portrait (taller than wide)
export const BACKEND_PORTRAIT_WIDTH = 2550;
export const BACKEND_PORTRAIT_HEIGHT = 3300;

// Backend dimensions for landscape (wider than tall)  
export const BACKEND_LANDSCAPE_WIDTH = 3300;
export const BACKEND_LANDSCAPE_HEIGHT = 2550;

// Helper function to get correct backend dimensions based on viewport
export const getBackendDimensions = (viewport: pdfjsLib.PageViewport | null) => {
    if (!viewport) {
        return { width: BACKEND_PORTRAIT_WIDTH, height: BACKEND_PORTRAIT_HEIGHT };
    }

    const isPortrait = viewport.height > viewport.width;
    return {
        width: isPortrait ? BACKEND_PORTRAIT_WIDTH : BACKEND_LANDSCAPE_WIDTH,
        height: isPortrait ? BACKEND_PORTRAIT_HEIGHT : BACKEND_LANDSCAPE_HEIGHT,
        orientation: isPortrait ? 'portrait' : 'landscape'
    };
};

// Add PDF dimensions constants (in points, 1 point = 1/72 inch)
export const PDF_POINTS_PER_INCH = 72;
export const US_LETTER_WIDTH_POINTS = 8.5 * PDF_POINTS_PER_INCH;  // 612 points
export const US_LETTER_HEIGHT_POINTS = 11 * PDF_POINTS_PER_INCH;  // 792 points

export type Rectangle = {
    id: string;
    x: number;
    y: number;
    width: number;
    height: number;
    fieldType: "text" | "table";
    pageNumber: number;
    extractedData?: any;
    columnPositions?: number[]; // Store as percentages (0-100)
    name?: string; // Add name field
};

// Convert display coordinates to backend coordinates
export const toBackendCoordinates = (
    x: number,
    y: number,
    width: number,
    height: number,
    viewport: pdfjsLib.PageViewport | null,
    canvasWidth: number,
    canvasHeight: number
) => {
    if (!viewport) return { x, y, width, height };

    // Calculate scale factors based on canvas dimensions
    const scaleX = BACKEND_WIDTH / canvasWidth;
    const scaleY = BACKEND_HEIGHT / canvasHeight;


    // console.log({ canvasHeight, canvasWidth, width, height, viewport, scaleX, scaleY })
    return {
        x: Math.round(x * scaleX),
        y: Math.round(y * scaleY),
        width: Math.round(width * scaleX),
        height: Math.round(height * scaleY),
    };
};

// Improved coordinate conversion that handles backend centering (not stretching)
export const toBackendCoordinatesImproved = (
    x: number,
    y: number,
    width: number,
    height: number,
    viewport: pdfjsLib.PageViewport | null,
    canvasWidth: number,
    canvasHeight: number
) => {
    if (!viewport) return { x, y, width, height };

    // Get correct backend dimensions based on PDF orientation
    const backendDims = getBackendDimensions(viewport);

    // Backend centers PDF within fixed canvas (like letterboxing), doesn't stretch
    // Calculate how backend scales and centers the PDF

    // Step 1: Calculate scale factor (same as backend uses)
    // Backend scales PDF to fit within its canvas while preserving aspect ratio
    const scaleX = backendDims.width / viewport.width;
    const scaleY = backendDims.height / viewport.height;
    const backendScale = Math.min(scaleX, scaleY); // Use smaller scale to fit within canvas

    // Step 2: Calculate scaled PDF dimensions in backend
    const scaledPdfWidth = viewport.width * backendScale;
    const scaledPdfHeight = viewport.height * backendScale;

    // Step 3: Calculate centering offsets in backend
    const offsetX = (backendDims.width - scaledPdfWidth) / 2;
    const offsetY = (backendDims.height - scaledPdfHeight) / 2;

    // Step 4: Convert browser coordinates to backend coordinates
    // First scale from browser canvas to PDF coordinates, then apply backend scaling and centering
    const pdfX = x * (viewport.width / canvasWidth);
    const pdfY = y * (viewport.height / canvasHeight);
    const pdfWidth = width * (viewport.width / canvasWidth);
    const pdfHeight = height * (viewport.height / canvasHeight);

    // Apply backend scaling and centering
    const backendX = pdfX * backendScale + offsetX;
    const backendY = pdfY * backendScale + offsetY;
    const backendWidth = pdfWidth * backendScale;
    const backendHeight = pdfHeight * backendScale;

    return {
        x: Math.round(backendX),
        y: Math.round(backendY),
        width: Math.round(backendWidth),
        height: Math.round(backendHeight),
    };
};

// Convert backend coordinates to display coordinates
export const toDisplayCoordinates = (
    x: number,
    y: number,
    width: number,
    height: number,
    viewport: pdfjsLib.PageViewport | null,
    canvasWidth: number,
    canvasHeight: number
) => {
    if (!viewport) {
        // console.log("No viewport available for coordinate conversion");
        return { x, y, width, height };
    }

    // Calculate scale factors based on canvas dimensions
    const scaleX = canvasWidth / BACKEND_WIDTH;
    const scaleY = canvasHeight / BACKEND_HEIGHT;

    const result = {
        x: x * scaleX,
        y: y * scaleY,
        width: width * scaleX,
        height: height * scaleY,
    };
    // console.log("Converted coordinates:", result);
    return result;
};

// Improved coordinate conversion from backend to display that handles centering
export const toDisplayCoordinatesImproved = (
    x: number,
    y: number,
    width: number,
    height: number,
    viewport: pdfjsLib.PageViewport | null,
    canvasWidth: number,
    canvasHeight: number
) => {
    if (!viewport) {
        // console.log("No viewport available for coordinate conversion");
        return { x, y, width, height };
    }

    // Get correct backend dimensions based on PDF orientation
    const backendDims = getBackendDimensions(viewport);

    // Reverse of toBackendCoordinatesImproved - convert from backend centered coordinates back to browser

    // Step 1: Calculate backend scaling and centering (same as forward conversion)
    const scaleX = backendDims.width / viewport.width;
    const scaleY = backendDims.height / viewport.height;
    const backendScale = Math.min(scaleX, scaleY);

    const scaledPdfWidth = viewport.width * backendScale;
    const scaledPdfHeight = viewport.height * backendScale;
    const offsetX = (backendDims.width - scaledPdfWidth) / 2;
    const offsetY = (backendDims.height - scaledPdfHeight) / 2;

    // Step 2: Remove centering offset and scale back to PDF coordinates
    const pdfX = (x - offsetX) / backendScale;
    const pdfY = (y - offsetY) / backendScale;
    const pdfWidth = width / backendScale;
    const pdfHeight = height / backendScale;

    // Step 3: Scale from PDF coordinates to browser canvas coordinates
    const canvasX = pdfX * (canvasWidth / viewport.width);
    const canvasY = pdfY * (canvasHeight / viewport.height);
    const canvasWidthResult = pdfWidth * (canvasWidth / viewport.width);
    const canvasHeightResult = pdfHeight * (canvasHeight / viewport.height);

    const result = {
        x: canvasX,
        y: canvasY,
        width: canvasWidthResult,
        height: canvasHeightResult,
    };
    return result;
};

// Convert extracted data to rectangles
export const convertToRectangles = (
    output: any[],
    viewport: pdfjsLib.PageViewport | null
): Rectangle[] => {
    if (!output || !Array.isArray(output)) {
        // console.log("Invalid output data:", output);
        return [];
    }

    // console.log("Converting output to rectangles:", output);
    return output.map((item: any) => {
        const displayCoords = toDisplayCoordinates(
            item.x0,
            item.y0,
            item.x1 - item.x0,
            item.y1 - item.y0,
            viewport,
            viewport?.width || 0,
            viewport?.height || 0
        );

        const rectangle: Rectangle = {
            id: item.rectangleId,
            x: displayCoords.x,
            y: displayCoords.y,
            width: displayCoords.width,
            height: displayCoords.height,
            fieldType: item.toolType === "ocrtables" ? "table" : "text",
            pageNumber: parseInt(item.pageNumber) + 1, // Convert from 0-based to 1-based
            extractedData: item,
        };
        // console.log("Created rectangle:", rectangle);
        return rectangle;
    });
};

// Draw rectangles on canvas
export const drawRectangles = (
    context: CanvasRenderingContext2D,
    rectangles: Rectangle[],
    selectedRectangle: Rectangle | null,
    scale: number
) => {
    // Clear the rectangle canvas
    context.clearRect(0, 0, context.canvas.width, context.canvas.height);

    // Draw all rectangles
    rectangles.forEach((rect) => {
        // Set style based on whether this is the selected rectangle
        const isSelected = selectedRectangle &&
            rect.x === selectedRectangle.x &&
            rect.y === selectedRectangle.y &&
            rect.width === selectedRectangle.width &&
            rect.height === selectedRectangle.height;

        if (isSelected) {
            context.strokeStyle = "#FF6B6B";
            context.lineWidth = 3;
            context.setLineDash([5, 5]);
        } else {
            context.strokeStyle = "blue";
            context.lineWidth = 2;
            context.setLineDash([]);
        }

        // Draw the rectangle
        context.strokeRect(
            rect.x * scale,
            rect.y * scale,
            rect.width * scale,
            rect.height * scale
        );

        // Draw the label
        context.fillStyle = "black";
        context.font = "12px Arial";
        const fieldName = rect.name || `${rect.fieldType} (${rect.x.toFixed(0)},${rect.y.toFixed(0)})`;
        const textWidth = context.measureText(fieldName).width;
        const textX = rect.x * scale + (rect.width * scale - textWidth) / 2;
        const textY = rect.y * scale + (rect.height * scale) / 2;
        context.fillText(fieldName, textX, textY);
    });
}; 